// src/pages/Events.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  dateFnsLocalizer,
  View,
  NavigateAction,
} from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styles from './Events.module.css';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Editor } from '@tinymce/tinymce-react';
import { Plus } from 'lucide-react';
import ContextualHelp from '../components/ContextualHelp';

interface EventType {
  id: string | number;
  title: string;
  start: Date;
  end: Date;
  rsvps: any[];
  roles: any[];
  [key: string]: any;
}

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});
const normalizeEvent = (raw: any) => ({
  id: raw.Id || raw.id,
  title: raw.Title || raw.title,
  start: raw.Start ? new Date(raw.Start) : (raw.start ? new Date(raw.start) : new Date()),
  end: raw.End ? new Date(raw.End) : (raw.end ? new Date(raw.end) : new Date()),
  description: raw.Description || raw.description,
  roles: (raw.Roles || raw.roles || []).map((r: any) => ({
    name: r.Name || r.name,
    icon: r.Icon || r.icon,
    capacity: r.Capacity || r.capacity,
    department: r.Department || r.department,
  })),
  rsvps: (raw.Rsvps || raw.rsvps || []).map((r: any) => ({
    username: r.Username || r.username,
    role: r.Role || r.role,
    attending: r.Attending !== undefined ? r.Attending : r.attending,
    status: r.Status || r.status || (r.Attending || r.attending ? 'Confirmed' : 'NotAttending'),
    avatarUrl: r.AvatarUrl || r.avatarUrl,
  })),
  eventImageUrl: raw.EventImageUrl || raw.eventImageUrl,
  discordChannelId: raw.DiscordChannelId || raw.discordChannelId,
  discordMessageId: raw.DiscordMessageId || raw.discordMessageId,
  recurrenceType: raw.RecurrenceType || raw.recurrenceType,
  recurrenceInterval: raw.RecurrenceInterval || raw.recurrenceInterval,
  recurrenceEndDate: raw.RecurrenceEndDate || raw.recurrenceEndDate,
  parentEventId: raw.ParentEventId || raw.parentEventId,
  leadingDepartmentId: raw.LeadingDepartmentId || raw.leadingDepartmentId,
  isLeadershipOnly: raw.IsLeadershipOnly || raw.isLeadershipOnly || false,
});
const getFullAvatarUrl = (avatarUrl: string | null | undefined): string | null => {
  if (!avatarUrl) return null;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
};


const Events: React.FC = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [availableRoles, setAvailableRoles] = useState<
  { name: string; icon: string; department?: string }[]
>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  // Existing state for event creation
  const [descriptionHtml, setDescriptionHtml] = useState('');
  // Updated: store multiple RSVP roles (each with name and capacity)
  const [selectedRoles, setSelectedRoles] = useState<
  { name: string; icon: string; capacity: number; department?: string }[]
>([]);
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Recurrence controls
  const [recurrenceType, setRecurrenceType] = useState<string>('none');
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>('');
  const [modalRefreshKey, setModalRefreshKey] = useState<number>(Date.now());
  const [rsvpRefreshCount, setRsvpRefreshCount] = useState(0);

  // Refresh user permissions on component mount to prevent stale cache issues
  useEffect(() => {
    refreshUser();
  }, []);

  const getUserScopes = () => {
    const scopes: string[] = [];
    console.log('User object in Events:', user);
    console.log('User role:', user?.role);
    console.log('User flags:', user?.flags);
    if (user?.role === 'Admin') scopes.push('createEvent');
    if (user?.role === 'Leader') scopes.push('createEvent');
    if (user?.flags?.includes('CanEditEvents')) scopes.push('createEvent');
    console.log('User scopes:', scopes);
    return scopes;
  };

  const userScopes = getUserScopes();
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | number | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<View>('month');
  const [events, setEvents] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: '',
    end: '',
  });
  const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';
  const fetchUpdatedEvent = async (eventId: string | number) => {
    try {
      const res = await axios.get(`/events/public/${eventId}`);
      const updatedEvent = normalizeEvent(res.data);
      setSelectedEvent(updatedEvent);
      setModalRefreshKey(Date.now()); // force render
    } catch (err) {
      console.error("Failed to fetch updated event:", err);
    }
  };
  // NEW: store the image in state as base64, so we can display + send it.
  const [eventImageUrl, setEventImageUrl] = useState<string>('');
  const [isLeadershipOnly, setIsLeadershipOnly] = useState<boolean>(false);

  // Converts a local datetime string (from input) into a UTC ISO string
  const toUtcIso = (value: string) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  useEffect(() => {
    axios
      .get('/events')
      .then((res) => {
        console.log('Fetched events:', res.data);
        const fetchedEvents = res.data.map((evt: any) => ({
          ...evt,
          id: evt.Id || evt.id,
          eventId: evt.Id || evt.id,
          EventId: evt.Id || evt.id,
          title: evt.Title || evt.title,
          Title: evt.Title || evt.title,
          start: new Date(evt.Start || evt.start),
          end: new Date(evt.End || evt.end),
          Start: evt.Start || evt.start,
          End: evt.End || evt.end,
        }));
        console.log('Processed events for calendar:', fetchedEvents);
        setEvents(fetchedEvents);
      })
      .catch((err) => console.error('Error fetching events:', err.response?.data || err.message));
  
    // Fetch RSVP options from position-based endpoint
    axios
      .get('/positions/rsvp-options')
      .then((res) => {
        // Add general attendance options at the beginning
        const generalOptions = [
          { name: 'Attending', icon: '✅', department: 'General', unlimited: true },
          { name: 'Tentative', icon: '❓', department: 'General', unlimited: true },
          { name: 'Not Attending', icon: '❌', department: 'General', unlimited: true }
        ];
        const rolesWithUnlimited = res.data.map((role: any) => ({
          ...role,
          unlimited: role.name === 'Standby' || role.name === 'Spectator'
        }));
        setAvailableRoles([...generalOptions, ...rolesWithUnlimited]);
      })
      .catch((err) => {
        console.error('Error loading position-based RSVP roles:', err.message);
        // Fallback to legacy endpoint if positions endpoint fails
        axios.get('/Events/rsvp-options')
          .then((res) => {
            const generalOptions = [
              { name: 'Attending', icon: '✅', department: 'General', unlimited: true },
              { name: 'Tentative', icon: '❓', department: 'General', unlimited: true },
              { name: 'Not Attending', icon: '❌', department: 'General', unlimited: true }
            ];
            const rolesWithUnlimited = res.data.map((role: any) => ({
              ...role,
              unlimited: role.name === 'Standby' || role.name === 'Spectator'
            }));
            setAvailableRoles([...generalOptions, ...rolesWithUnlimited]);
          })
          .catch((err2) => console.error('Error loading legacy RSVP roles:', err2.message));
      });
  }, []);
  
  const handleSelectEvent = async (event: any) => {
    navigate(`/events/${event.eventId || event.EventId || event.id}`);
  };  
  
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setSelectedEventId(null); // Reset ID as well
  };
  

  // For managing multiple RSVP role selections:
  const toggleRoleSelection = (name: string, icon: string) => {
    const existing = selectedRoles.find((r) => r.name === name);
  
    if (existing) {
      setSelectedRoles(selectedRoles.filter((r) => r.name !== name));
    } else {
      const roleMeta = availableRoles.find((r) => r.name === name);
      const department = roleMeta?.department; // ✅ no "?? null"
  
      setSelectedRoles([
        ...selectedRoles,
        {
          name,
          icon,
          capacity: 1,
          department // ✅ pass department along
        }
      ]);
    }
  };  
 
  const updateRoleCapacity = (name: string, newCap: number) => {
    setSelectedRoles(
      selectedRoles.map((r) =>
        r.name === name ? { ...r, capacity: newCap } : r
      )
    );
  };

  // NEW: handle the event image upload (like your PostForm example).
  const handleEventImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
  
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
  
      const formData = new FormData();
      formData.append('image', file); // ✅ Must match `[FromForm] IFormFile image`
  
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/Events/upload/image`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${token}` // ✅ inject your token if needed
          }
        });
  
        const { imageUrl } = await res.json();
  
        if (imageUrl) {
          setEventImageUrl(imageUrl); // ✅ Save the uploaded event image path
        } else {
          alert('Upload failed: Missing imageUrl');
        }
      } catch (err) {
        console.error('Event image upload failed:', err);
        alert('Upload failed');
      }
    };
  
    input.click();
  };
  
  const handleRsvp = async (eventId: string | number, roleName: string, status: string = 'Confirmed') => {
    try {
      console.log('🔍 RSVP user object:', user);
      console.log('🔍 RSVP username being sent:', user?.username);
      console.log('🔍 RSVP status:', status);
      
      // For backwards compatibility: attending = true if Confirmed or Tentative
      const attending = status === 'Confirmed' || status === 'Tentative';
      
      await axios.post('/events/rsvp', {
        eventId,
        username: user?.username,
        attending,  // Keep for backwards compatibility
        status,     // NEW: Send status enum
        role: roleName,
        source: 'web',
        discordId: user?.discordId,
        avatarUrl: user?.avatarUrl,
      });
  
      // Re-fetch the full event data since POST /events/rsvp returns a confirmation string
      const eventRes = await axios.get(`/events/${eventId}`);
      const updatedEvent = normalizeEvent(eventRes.data);
      setSelectedEvent(updatedEvent);
      setEvents((prev) =>
        prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e))
      );
      setModalRefreshKey(Date.now());

      // Note: Discord bot notification is handled by the backend
    } catch (err) {
      console.error('RSVP failed:', err);
      alert('Failed to update RSVP');
    }
  };
  
  // Custom event component to ensure title is displayed
  return (
    <div className={styles.eventsContainer}>
      <div className={styles.calendarWrapper}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 className={styles.title}>Event Calendar</h1>
            <ContextualHelp featureArea="events" />
          </div>
          {userScopes.includes('createEvent') && (
            <button className="button" onClick={() => navigate('/events/create')}>
              <Plus size={18} /> Create Event
            </button>
          )}
        </div>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          date={currentDate}
          onNavigate={(newDate: Date) => setCurrentDate(newDate)}
          view={currentView}
          onView={(view: View) => setCurrentView(view)}
          views={{ month: true, week: true, day: true, agenda: true }}
          defaultView="month"
          toolbar={true}
          style={{ height: 600 }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={(event: any) => ({
            className: event.isLeadershipOnly ? 'leadership-event' : 'regular-event'
          })}
        />
      </div>

      {/* Create Event Modal */}
      {isCreateModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsCreateModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Create Event</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();

                const startUtc = toUtcIso(newEvent.start);
                const endUtc = toUtcIso(newEvent.end);
                if (!startUtc || !endUtc) {
                  alert('Invalid date format. Please check start and end times.');
                  return;
                }

                try {
                  const payload = {
                    title: newEvent.title,
                    start: startUtc,
                    end: endUtc,
                    description: descriptionHtml,
                    roles: selectedRoles,
                    eventImageUrl: eventImageUrl,
                    recurrenceType,
                    recurrenceInterval: recurrenceType === 'none' ? null : recurrenceInterval,
                    recurrenceEndDate:
                      recurrenceType === 'none' || !recurrenceEndDate
                        ? null
                        : new Date(recurrenceEndDate).toISOString(),
                    isLeadershipOnly: isLeadershipOnly,
                  };

                  console.log('Sending event payload:', payload);
                  if (isEditMode && selectedEvent?.id) {
                    await axios.put(`/events/${selectedEvent.id}`, payload);
                  } else {
                    await axios.post('/events', payload);
                  }

                  setIsCreateModalOpen(false);
                  setNewEvent({ title: '', start: '', end: '' });
                  setDescriptionHtml('');
                  setSelectedRoles([]);
                  setEventImageUrl('');
                  setRecurrenceType('none');
                  setRecurrenceInterval(1);
                  setRecurrenceEndDate('');
                  setIsEditMode(false);
                  setSelectedEvent(null);
                  setIsLeadershipOnly(false);

                  const res = await axios.get('/events');
                  const fetchedEvents = res.data.map((evt: any) => ({
                    ...evt,
                    id: evt.Id || evt.id,
                    start: new Date(evt.Start || evt.start),
                    end: new Date(evt.End || evt.end),
                  }));
                  console.log('✅ Events refreshed after create:', fetchedEvents);
                  setEvents(fetchedEvents);
                } catch (err) {
                  console.error('Failed to create event:', err);
                  alert('Failed to create or update event.');
                }
              }}
            >
              <div className={styles.modalRow}>
                <label htmlFor="eventTitle">Title</label>
                <input
                  id="eventTitle"
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  required
                />
              </div>

              <label>Description:</label>
              <Editor
                apiKey={tinymceApiKey}
                value={descriptionHtml}
                onEditorChange={(newContent) => setDescriptionHtml(newContent)}
                init={{
                  height: 300,
                  menubar: true,
                  plugins: [
                    'advlist autolink lists link image media charmap preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime table help wordcount emoticons',
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic underline forecolor backcolor | ' +
                    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                    'link image media emoticons | removeformat | code fullscreen help',
                  automatic_uploads: true,
                  images_upload_url: `${process.env.REACT_APP_API_URL}/upload/image`,
                  file_picker_types: 'image',
                  file_picker_callback: (
                    cb: (url: string, meta?: { alt?: string }) => void,
                    _val: string,
                    meta: { filetype: string }
                  ) => {
                    if (meta.filetype === 'image') {
                      const input = document.createElement('input');
                      input.setAttribute('type', 'file');
                      input.setAttribute('accept', 'image/*');
                      input.onchange = () => {
                        const file = input.files?.[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('image', file);
                          fetch(`${process.env.REACT_APP_API_URL}/Events/upload/image`, {
                            method: 'POST',
                            body: formData,
                          })
                            .then((res) => res.json())
                            .then((data) => cb(data.imageUrl, { alt: file.name }))
                            .catch((err) => console.error('Upload failed:', err));
                        }
                      };
                      input.click();
                    }
                  },
                }}
              />

              <div className={styles.modalRow}>
                <label htmlFor="eventStart">Start Time</label>
                <input
                  id="eventStart"
                  type="datetime-local"
                  value={newEvent.start}
                  onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
                  required
                />
              </div>

              <div className={styles.modalRow}>
                <label htmlFor="eventEnd">End Time</label>
                <input
                  id="eventEnd"
                  type="datetime-local"
                  value={newEvent.end}
                  onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
                  required
                />
              </div>

              <div className={styles.modalRow}>
                <label htmlFor="recurrenceType">Recurrence</label>
                <select
                  id="recurrenceType"
                  value={recurrenceType}
                  onChange={(e) => setRecurrenceType(e.target.value)}
                >
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {recurrenceType !== 'none' && (
                <>
                  <div className={styles.modalRow}>
                    <label htmlFor="recurrenceInterval">Repeat every</label>
                    <input
                      id="recurrenceInterval"
                      type="number"
                      min={1}
                      value={recurrenceInterval}
                      onChange={(e) =>
                        setRecurrenceInterval(
                          Math.max(1, parseInt(e.target.value || '1', 10))
                        )
                      }
                    />
                  </div>

                  <div className={styles.modalRow}>
                    <label htmlFor="recurrenceEndDate">End date (optional)</label>
                    <input
                      id="recurrenceEndDate"
                      type="date"
                      value={recurrenceEndDate}
                      onChange={(e) => setRecurrenceEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className={styles.modalRow} style={{ alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="isLeadershipOnly"
                  checked={isLeadershipOnly}
                  onChange={(e) => setIsLeadershipOnly(e.target.checked)}
                  style={{ width: 'auto', margin: 0 }}
                />
                <label htmlFor="isLeadershipOnly" style={{ margin: 0, fontWeight: 'normal' }}>
                  🔒 Leadership Only Event (Tier 3+ only)
                </label>
              </div>

              <div className={styles.modalRow}>
                <label>RSVP Roles Needed:</label>
                <button type="button" className="button" onClick={() => setShowRsvpModal(true)}>
                  Edit RSVP Roles ({selectedRoles.length} selected)
                </button>
              </div>

              {selectedEvent && selectedRoles.length > 0 && (
              <div className={styles.roleGrid} key={rsvpRefreshCount}>
                {selectedEvent.roles.map((role: any) => (
                  <div key={role.name} className={styles.roleCard}>
                    <div className={styles.roleHeader}>
                      <span className={styles.roleIcon}>{role.icon}</span>
                      <span className={styles.roleLabel}>{role.name}</span>
                      <span className={styles.roleCount}>{role.capacity === 0 || role.capacity === 9999 ? '∞' : `x${role.capacity}`}</span>
                    </div>
              
                    <div className={styles.roleStatus}>
                      {role.attending}/{role.capacity === 0 || role.capacity === 9999 ? '∞' : role.capacity} attending
                    </div>
              
                    <ul className={styles.attendeeList}>
                      {selectedEvent.rsvps
                        ?.filter((r: any) => r.role === role.name && r.attending)
                        .map((attendee: any) => {
                          const avatarUrl = attendee.avatarUrl?.startsWith('http')
                            ? attendee.avatarUrl
                            : `${process.env.REACT_APP_API_URL}${attendee.avatarUrl}`;
              
                          return (
                            <li key={attendee.username} className={styles.attendeeItem}>
                              {avatarUrl && (
                                <img
                                  src={avatarUrl}
                                  alt={attendee.username}
                                  className={styles.attendeeAvatar}
                                />
                              )}
                              <span className={styles.attendeeName}>
                                {attendee.username}
                                {attendee.username === user?.username && (
                                  <span className={styles.rsvpBadge}>RSVP’d</span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
              
                    <div className={styles.roleActions}>
                      {(() => {
                        // Count confirmed and tentative RSVPs (standby doesn't count toward capacity)
                        const countingRsvps = selectedEvent.rsvps?.filter(
                          (r: any) => r.role === role.name && (r.status === 'Confirmed' || r.status === 'Tentative')
                        ) || [];
                        
                        const userRsvp = selectedEvent.rsvps?.find(
                          (r: any) => r.username === user?.username && r.role === role.name
                        );
                        
                        const userStatus = userRsvp?.status;
                        const isFull = role.capacity > 0 && countingRsvps.length >= role.capacity;
                        const isConfirmedOrTentative = userStatus === 'Confirmed' || userStatus === 'Tentative';
              
                        if (user && userRsvp && isConfirmedOrTentative) {
                          return (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <span className={styles.statusBadge} style={{
                                backgroundColor: userStatus === 'Confirmed' ? '#4caf50' : '#ff9800',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.85em'
                              }}>
                                {userStatus === 'Confirmed' ? '✓ Confirmed' : '⏰ Tentative'}
                              </span>
                              <button
                                className="button outline"
                                style={{ fontSize: '0.9em', padding: '4px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'NotAttending')
                                }
                              >
                                Cancel
                              </button>
                              <button
                                className="button outline"
                                style={{ fontSize: '0.9em', padding: '4px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'Standby')
                                }
                              >
                                → Standby
                              </button>
                            </div>
                          );
                        }
              
                        if (user && !isConfirmedOrTentative && !isFull) {
                          return (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              <button
                                className="button"
                                style={{ fontSize: '0.9em', padding: '6px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'Confirmed')
                                }
                              >
                                ✓ Confirm
                              </button>
                              <button
                                className="button outline"
                                style={{ fontSize: '0.9em', padding: '6px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'Tentative')
                                }
                              >
                                ⏰ Tentative
                              </button>
                              <button
                                className="button outline"
                                style={{ fontSize: '0.9em', padding: '6px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'Standby')
                                }
                              >
                                ⏳ Standby
                              </button>
                            </div>
                          );
                        }
                        
                        if (user && userStatus === 'Standby') {
                          return (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span className={styles.statusBadge} style={{
                                backgroundColor: '#9e9e9e',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.85em'
                              }}>
                                ⏳ Standby
                              </span>
                              <button
                                className="button outline"
                                style={{ fontSize: '0.9em', padding: '4px 12px' }}
                                onClick={() =>
                                  handleRsvp(selectedEvent.id, role.name, 'NotAttending')
                                }
                              >
                                Cancel
                              </button>
                            </div>
                          );
                        }
              
                        return (
                          <button className="button outline" disabled>
                            {isFull ? 'Full' : 'RSVP'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>              
              )}

              {/* NEW: Button + preview for event image */}
              <div className={styles.modalRow}>
                <label>Event Image</label>
                <button type="button" className="button outline" onClick={handleEventImageUpload}>
                  Upload Event Image
                </button>
              </div>
              {eventImageUrl && (
                <div style={{ margin: '1rem 0' }}>
                  <img
                    src={eventImageUrl}
                    alt="Event Preview"
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="submit" className="button">
                  Create
                </button>
                <button
                  type="button"
                  className="button outline"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setIsEditMode(false);
                    setSelectedEvent(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRsvpModal && (
        <div className={styles.modalOverlay} onClick={() => setShowRsvpModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h4>Select RSVP Roles</h4>
            <div className={styles.rsvpGrid}>
            {availableRoles.map((role) => {
                const isSelected = selectedRoles.find((r) => r.name === role.name);
                return (
                  <div key={role.name} className={styles.rsvpItem}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => toggleRoleSelection(role.name, role.icon)}
                      />
                      <span>
                        {role.icon} {role.name}
                      </span>
                    </label>
                    {isSelected && (
                      <input
                        type="number"
                        min={1}
                        value={isSelected.capacity}
                        onChange={(e) => updateRoleCapacity(role.name, parseInt(e.target.value))}
                        className={styles.capacityInput}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button className="button" onClick={() => setShowRsvpModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && selectedEvent && (
        <div key={modalRefreshKey} className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2>{selectedEvent.title}</h2>
            <p>
              <strong>Start:</strong> {new Date(selectedEvent.start).toLocaleString()}
            </p>
            <p>
              <strong>End:</strong> {new Date(selectedEvent.end).toLocaleString()}
            </p>
            
            {/* NEW: Show image if available */}
            {selectedEvent.eventImageUrl && (
              <div style={{ margin: '1rem 0' }}>
                <img
                  src={selectedEvent.eventImageUrl}
                  alt="Event"
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              </div>
            )}
            
            {selectedEvent.description && (
              <div className={styles.eventDescription}>
                <strong>Description:</strong>
                <div dangerouslySetInnerHTML={{ __html: selectedEvent.description }} />
              </div>
            )}
            {Array.isArray(selectedEvent.roles) && selectedEvent.roles.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <strong>RSVP Roles:</strong>
                <div className={styles.roleGrid}>
                  {selectedEvent.roles.map((role: any) => {
                    const attendingUsers = selectedEvent.rsvps?.filter(
                      (r: any) => r.role === role.name && r.attending
                    ) || [];

                    const alreadyRsvped = attendingUsers.some(
                      (a: any) => a.username === user?.username
                    );

                    const isFull = role.capacity > 0 && attendingUsers.length >= role.capacity;

                    return (
                      <div key={role.name} className={styles.roleCard}>
                        <div className={styles.roleHeader}>
                          <span className={styles.roleIcon}>{role.icon}</span>
                          <span className={styles.roleLabel}>{role.name}</span>
                          <span className={styles.roleCount}>{role.capacity === 0 || role.capacity === 9999 ? '∞' : `x${role.capacity}`}</span>
                        </div>

                        <div className={styles.roleStatus}>
                          {attendingUsers.length}/{role.capacity === 0 || role.capacity === 9999 ? '∞' : role.capacity} attending
                        </div>

                        <ul className={styles.attendeeList}>
                          {attendingUsers.map((a: any) => {
                            const avatarUrl = a.avatarUrl?.startsWith('http')
                              ? a.avatarUrl
                              : `${process.env.REACT_APP_API_URL}${a.avatarUrl}`;

                            return (
                              <li key={a.username} className={styles.attendeeRow}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {avatarUrl && (
                                    <img
                                      src={avatarUrl}
                                      alt={a.username}
                                      className={styles.attendeeAvatar}
                                    />
                                  )}
                                  <span className={styles.attendeeName}>
                                    {a.username}
                                    {a.username === user?.username && (
                                      <span className={styles.rsvpBadge}>RSVP’d</span>
                                    )}
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>

                        <div className={styles.roleActions}>
                          {user && alreadyRsvped && (
                            <button
                              className="button outline"
                              onClick={async () => {
                                try {
                                  const response = await axios.post('/events/rsvp', {
                                    eventId: selectedEvent.id,
                                    username: user.username,
                                    attending: false,
                                    role: role.name,
                                  });

                                  const updatedEvent = normalizeEvent(response.data);
                                  setSelectedEvent(updatedEvent);
                                  setEvents((prev) =>
                                    prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e))
                                  );
                                  setModalRefreshKey(Date.now());
                                  await fetchUpdatedEvent(selectedEvent.id);
                                } catch (err) {
                                  console.error('Failed to cancel RSVP:', err);
                                  alert('Cancel failed');
                                }
                              }}
                            >
                              Cancel RSVP
                            </button>
                          )}

                          {user && !alreadyRsvped && !isFull && (
                            <button
                              className="button"
                              onClick={async () => {
                                try {
                                  const response = await axios.post('/events/rsvp', {
                                    eventId: selectedEvent.id,
                                    username: user.username,
                                    attending: true,
                                    role: role.name,
                                  });

                                  // Backend handles Discord bot notification
                                  const updatedEvent = normalizeEvent(response.data);
                                  setSelectedEvent(updatedEvent);
                                  setEvents((prev) =>
                                    prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e))
                                  );
                                  setModalRefreshKey(Date.now());
                                  await fetchUpdatedEvent(selectedEvent.id);
                                } catch (err) {
                                  console.error('Failed to RSVP:', err);
                                  alert('RSVP failed');
                                }
                              }}
                            >
                              RSVP for {role.name}
                            </button>
                          )}

                          {user && !alreadyRsvped && isFull && (
                            <button className="button outline" disabled>
                              Full
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Place Delete next to Close */}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end',
                marginTop: '2rem',
              }}
            >
              {userScopes.includes('createEvent') && (
                <>
                  <button
                    className="button"
                    onClick={() => {
                      setIsModalOpen(false); // close the view modal
                      setIsEditMode(true);
                      setNewEvent({
                        title: selectedEvent.title,
                        start: new Date(selectedEvent.start).toISOString().slice(0, 16),
                        end: new Date(selectedEvent.end).toISOString().slice(0, 16),
                      });
                      setDescriptionHtml(selectedEvent.description || '');
                      setSelectedRoles(selectedEvent.roles || []);
                      setEventImageUrl(selectedEvent.eventImageUrl || '');
                      setIsCreateModalOpen(true); // open edit modal
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="button danger"
                    onClick={async () => {
                      let deleteAllOccurrences = false;
                      
                      console.log('Selected event for deletion:', selectedEvent);
                      
                      // Check if this is a recurring event (has parentEventId or recurrenceType)
                      const isRecurring = selectedEvent.parentEventId || 
                                        (selectedEvent.recurrenceType && selectedEvent.recurrenceType !== 'none');
                      
                      if (isRecurring) {
                        const userChoice = prompt(
                          'This is a recurring event.\n\n' +
                          'Type "all" to delete ALL occurrences (entire series)\n' +
                          'Type "one" to delete ONLY this instance\n' +
                          'Or click Cancel to abort',
                          'all'
                        );
                        
                        if (userChoice === null) {
                          return; // User cancelled
                        }
                        
                        const choice = userChoice.toLowerCase().trim();
                        
                        if (choice === 'all') {
                          deleteAllOccurrences = true;
                        } else if (choice === 'one') {
                          deleteAllOccurrences = false;
                        } else {
                          alert('Invalid choice. Please type "all" or "one".');
                          return;
                        }
                      } else {
                        // Single event - simple confirmation
                        if (!window.confirm('Are you sure you want to delete this event?')) {
                          return;
                        }
                      }
                      
                      try {
                        await axios.delete(`/events/${selectedEvent.id}?deleteAllOccurrences=${deleteAllOccurrences}`);
                        const res = await axios.get('/events');
                        setEvents(
                          res.data.map((evt: any) => ({
                            ...evt,
                            id: evt.Id || evt.id,
                            start: new Date(evt.Start || evt.start),
                            end: new Date(evt.End || evt.end),
                          }))
                        );
                        closeModal();
                      } catch (err) {
                        console.error('Failed to delete event:', err);
                        alert('Failed to delete event.');
                      }
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
              <button className="button outline" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
